import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const formatFechaHoraConsulta = (value) => {
  if (!value) return null;
  const fecha = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleString('es-EC', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const EVENT_TYPES_BASE = [
  { value: '', label: 'Todos' },
  { value: 'RONDA', label: 'Ronda' },
  { value: 'NOVEDAD', label: 'Novedad' },
  { value: 'VISITANTE', label: 'Visitante' },
  { value: 'VEHICULO', label: 'Vehículo' },
  { value: 'INFO-VISITANTE', label: 'Info visitante' },
  { value: 'INFO-VEHICULO', label: 'Info vehículo' }
];

/** Etiquetas para tipos ligados a opciones de bitácora personalizada (mismo criterio que el menú lateral). */
const TIPO_EVENTO_PERSONALIZADO_LABEL = {
  INGRESO_CHARLY: 'Ingresos Charly',
  ALFA_TANGO_1: 'Alfa Tango 1',
  ALFA_TANGO_2: 'Alfa Tango 2',
  TANGO_3: 'Tango 3'
};

const ORDEN_TIPOS_PERSONALIZADOS = ['INGRESO_CHARLY', 'ALFA_TANGO_1', 'ALFA_TANGO_2', 'TANGO_3'];

/** Debe coincidir con backend: tipos que solo existen en el desplegable si el puesto tiene la opción en catálogo. */
const TIPOS_EVENTO_SOLO_CON_CATALOGO_PERSONALIZADO = new Set(Object.keys(TIPO_EVENTO_PERSONALIZADO_LABEL));
const AT2_SLOT_LABELS = {
  carga_elementos: 'Foto(s) elementos (carga)',
  carga_guia: 'Foto(s) guía (carga)',
  carga_dolly: 'Foto(s) Dolly / carretas / contenedor',
  especial_guia: 'Foto(s) guía especial',
  especial_representante: 'Foto(s) representante',
  especial_dolly: 'Foto(s) Dolly / carretas / contenedor',
  datafast_documento: 'Foto(s) documento Datafast',
  datafast_carga: 'Foto(s) carga Datafast',
  ingreso_unidad: 'Foto(s) camión / dolly / carreta',
  salida_camion: 'Foto(s) camión / unidad (histórico)',
  salida_dist_guia: 'Foto(s) guía master (servicios especiales)'
};

/** Slot 0-based en código → "Servicio 1" para el usuario en consultas. */
const labelAt2SlotConsulta = (slot) => {
  const s = String(slot || '').trim();
  const mxg = /^salida_xfer_svc_(\d+)_guia$/i.exec(s);
  if (mxg) return `Servicio especial ${parseInt(mxg[1], 10) + 1} — Guía`;
  const mxb = /^salida_xfer_svc_(\d+)_bultos$/i.exec(s);
  if (mxb) return `Servicio especial ${parseInt(mxb[1], 10) + 1} — Bultos`;
  const mdg = /^salida_dist_svc_(\d+)_guia$/i.exec(s);
  if (mdg) return `Servicio especial ${parseInt(mdg[1], 10) + 1} — Guía`;
  const mdb = /^salida_dist_svc_(\d+)_bultos$/i.exec(s);
  if (mdb) return `Servicio especial ${parseInt(mdb[1], 10) + 1} — Bultos`;
  const mg = /^salida_svc_(\d+)_guia$/i.exec(s);
  if (mg) return `Servicio ${parseInt(mg[1], 10) + 1} — Guía master`;
  const mb = /^salida_svc_(\d+)_bultos$/i.exec(s);
  if (mb) return `Servicio ${parseInt(mb[1], 10) + 1} — Bultos`;
  return AT2_SLOT_LABELS[s] || s;
};

/** Orden legible: mismo servicio con guía antes que bultos (el índice interno sigue siendo 0-based). */
const compareAt2FotoSlotsConsulta = (a, b) => {
  const ordenParte = (slot) => {
    const s = String(slot || '');
    const m = /^(?:salida_xfer_svc|salida_dist_svc|salida_svc)_(\d+)_(guia|bultos)$/i.exec(s);
    if (!m) return { prefix: s, idx: 0, parte: 0 };
    const prefix = s.replace(/_\d+_(guia|bultos)$/i, '');
    return {
      prefix,
      idx: parseInt(m[1], 10),
      parte: /^guia$/i.test(m[2]) ? 0 : 1
    };
  };
  const pa = ordenParte(a);
  const pb = ordenParte(b);
  if (pa.prefix !== pb.prefix) return String(pa.prefix).localeCompare(String(pb.prefix));
  if (pa.idx !== pb.idx) return pa.idx - pb.idx;
  if (pa.parte !== pb.parte) return pa.parte - pb.parte;
  return String(a).localeCompare(String(b));
};

const buildAbsoluteUrl = (path, baseOrigin) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseOrigin) return path;
  if (path.startsWith('/')) {
    return `${baseOrigin}${path}`;
  }
  return `${baseOrigin}/${path}`;
};

const emptyFotosResolved = (fotoFirmaResolved) => ({
  foto_url_resolved: null,
  foto_urls_resolved: [],
  foto_grupos_resolved: [],
  foto_firma_url_resolved: fotoFirmaResolved
});

/** JSON vacío o sin URLs no cuenta como foto (evita intentar cargar "{}"). */
const tieneFotoUrlUtil = (fotoUrl) => {
  const s = String(fotoUrl ?? '').trim();
  if (!s || s === '{}' || s === '[]') return false;
  if (s.startsWith('{') || s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      if (!parsed || typeof parsed !== 'object') return false;
      const urls = Object.values(parsed)
        .flatMap((item) => (Array.isArray(item) ? item : []))
        .filter((u) => String(u || '').trim());
      return urls.length > 0;
    } catch (_) {
      return false;
    }
  }
  return true;
};

const getResolvedFotosForEvento = (evento, baseOrigin) => {
  const tipoEvento = String(evento?.tipo_evento || '').toUpperCase();
  const rawFoto = evento?.foto_url;
  const fotoFirmaResolved = buildAbsoluteUrl(evento?.foto_firma_url, baseOrigin);
  const rawTrim = String(rawFoto ?? '').trim();

  if (!rawTrim) {
    return emptyFotosResolved(fotoFirmaResolved);
  }

  // Compatibilidad: solo ALFA_TANGO_2 usa JSON en foto_url.
  if (tipoEvento === 'ALFA_TANGO_2' && (rawTrim.startsWith('{') || rawTrim.startsWith('['))) {
    if (rawTrim === '{}' || rawTrim === '[]') {
      return emptyFotosResolved(fotoFirmaResolved);
    }
    try {
      const parsed = JSON.parse(rawTrim);
      if (parsed && typeof parsed === 'object') {
        const urls = Object.values(parsed)
          .flatMap((item) => (Array.isArray(item) ? item : []))
          .map((u) => buildAbsoluteUrl(u, baseOrigin))
          .filter(Boolean);
        const grupos = Object.entries(parsed)
          .map(([slot, item]) => ({
            slot,
            label: labelAt2SlotConsulta(slot),
            urls: (Array.isArray(item) ? item : []).map((u) => buildAbsoluteUrl(u, baseOrigin)).filter(Boolean)
          }))
          .filter((g) => g.urls.length > 0)
          .sort((a, b) => compareAt2FotoSlotsConsulta(a.slot, b.slot));
        if (urls.length > 0) {
          return {
            foto_url_resolved: null,
            foto_urls_resolved: urls,
            foto_grupos_resolved: grupos,
            foto_firma_url_resolved: fotoFirmaResolved
          };
        }
        return emptyFotosResolved(fotoFirmaResolved);
      }
    } catch (_) {
      return emptyFotosResolved(fotoFirmaResolved);
    }
  }

  if (!tieneFotoUrlUtil(rawFoto)) {
    return emptyFotosResolved(fotoFirmaResolved);
  }

  return {
    foto_url_resolved: buildAbsoluteUrl(rawFoto, baseOrigin),
    foto_urls_resolved: [],
    foto_grupos_resolved: [],
    foto_firma_url_resolved: fotoFirmaResolved
  };
};

const parseObservacionPipeAt2 = (observacion, coincide) => {
  const raw = String(observacion || '').trim();
  if (!raw.includes(' | ')) return null;
  const lower = raw.toLowerCase();
  if (!coincide(lower)) return null;

  const partes = raw.split(/\s*\|\s*/).map((p) => p.trim()).filter(Boolean);
  if (partes.length < 2) return null;

  const titulo = partes[0];
  const campos = [];
  let observacionesLibre = '';

  for (let i = 1; i < partes.length; i += 1) {
    const p = partes[i];
    const idx = p.indexOf(':');
    if (idx === -1) {
      campos.push({ label: 'Detalle', value: p });
      continue;
    }
    const label = p.slice(0, idx).trim();
    const value = p.slice(idx + 1).trim();
    const labelLow = label.toLowerCase();
    if (
      labelLow === 'observaciones' ||
      labelLow.startsWith('observaciones ') ||
      labelLow === 'detalle adicional'
    ) {
      observacionesLibre = value;
    } else {
      campos.push({ label, value });
    }
  }

  return { titulo, campos, observacionesLibre };
};

/** Descompone observación pipe de Ingreso Z. Aire → Z. Distribución para consultas. */
const parseObservacionIngresoAireDistrib = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('ingreso') &&
      lower.includes('aire') &&
      lower.includes('distrib') &&
      !lower.includes('salida - z. distrib')
  );

/** Descompone observación pipe de Salida Z. Distribución → Z. Aire para consultas. */
const parseObservacionSalidaDistAire = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('salida') &&
      lower.includes('distrib') &&
      lower.includes('aire') &&
      !lower.includes('transferencia') &&
      !/\bz\.\s*r\b/.test(lower)
  );

/** Descompone observación pipe de Salida Z. Distribución → Z. Transferencia para consultas. */
const parseObservacionSalidaDistTransferencia = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('salida') &&
      lower.includes('distrib') &&
      lower.includes('transferencia') &&
      !lower.includes('z. aire') &&
      !/\bz\.\s*r\b/.test(lower)
  );

/** Descompone observación pipe de Salida Z. Distribución → Z. R para consultas. */
const parseObservacionSalidaDistR = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('salida') &&
      lower.includes('distrib') &&
      /\bz\.\s*r\b/.test(lower) &&
      !lower.includes('z. aire')
  );

/** Descompone observación pipe de Ingreso Z. Alfa → Z. Distribución para consultas. */
const parseObservacionIngresoAlfaDistrib = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('ingreso') &&
      lower.includes('alfa') &&
      lower.includes('distrib') &&
      !lower.includes('z. aire') &&
      !lower.includes('salida - z. distrib')
  );

/** Descompone observación pipe de Salida Z. Distribución → Z. Alfa para consultas. */
const parseObservacionSalidaDistAlfa = (observacion) =>
  parseObservacionPipeAt2(
    observacion,
    (lower) =>
      lower.includes('salida') &&
      lower.includes('distrib') &&
      lower.includes('z. alfa') &&
      !lower.includes('z. aire') &&
      !/\bz\.\s*r\b/.test(lower)
  );

/**
 * Resumen AT2 para grilla: dirección (Ingreso/Salida) en Tipo; zonas o subtipo en Observación.
 * El detalle operativo completo queda en el modal.
 */
const parseAlfaTango2ResumenTabla = (observacion) => {
  const obs = String(observacion || '').trim();
  if (!obs) return { direccion: null, detalle: '—' };

  const primera = obs.includes(' | ') ? obs.split(/\s*\|\s*/)[0].trim() : obs;
  const norm = primera
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

  if (norm.includes('ingreso') && norm.includes('z. aire') && norm.includes('z. distrib')) {
    return { direccion: 'Ingreso', detalle: 'Z. Aire a Z. Distribución' };
  }
  if (norm.includes('ingreso') && norm.includes('z. alfa') && norm.includes('z. distrib')) {
    return { direccion: 'Ingreso', detalle: 'Z. Alfa a Z. Distribución' };
  }
  if (norm.includes('salida') && norm.includes('z. distrib') && norm.includes('z. aire')) {
    return { direccion: 'Salida', detalle: 'Z. Distribución a Z. Aire' };
  }
  if (norm.includes('salida') && norm.includes('z. distrib') && norm.includes('z. alfa')) {
    return { direccion: 'Salida', detalle: 'Z. Distribución a Z. Alfa' };
  }
  if (norm.includes('salida') && norm.includes('z. distrib') && /\bz\.\s*r\b/.test(norm)) {
    return { direccion: 'Salida', detalle: 'Z. Distribución a Z. R' };
  }
  if (norm.includes('salida') && norm.includes('z. distrib') && norm.includes('transferencia')) {
    return { direccion: 'Salida', detalle: 'Z. Distribución a Z. Transferencia' };
  }
  if (norm.includes('ingreso') && norm.includes('carga')) {
    return { direccion: 'Ingreso', detalle: 'Carga' };
  }
  if (norm.includes('ingreso') && (norm.includes('guia especial') || norm.includes('guía especial'))) {
    return { direccion: 'Ingreso', detalle: 'Guía especial' };
  }
  if (norm.includes('ingreso') && norm.includes('datafast')) {
    return { direccion: 'Ingreso', detalle: 'Código Datafast' };
  }
  if (norm.includes('salida') && norm.includes('unidad')) {
    return { direccion: 'Salida', detalle: 'Unidad / camión' };
  }
  if (norm.includes('alfa tango 2') && norm.includes('salida')) {
    return { direccion: 'Salida', detalle: 'Camión / unidad (Alfa 2)' };
  }
  if (norm.includes('alfa tango 2') && norm.includes('ingreso')) {
    return { direccion: 'Ingreso', detalle: '—' };
  }

  const m = /^Alfa Tango 2\s*-\s*(Ingreso|Salida)\s*-\s*(.+)$/i.exec(primera);
  if (m) {
    const direccion = m[1] === 'Ingreso' ? 'Ingreso' : 'Salida';
    const resto = m[2].trim();
    if (resto.toLowerCase().startsWith('z.')) {
      const zonas = resto.replace(/^Salida\s*-\s*/i, '').replace(/^Ingreso\s*-\s*/i, '').trim();
      return { direccion, detalle: zonas || resto };
    }
    return { direccion, detalle: resto };
  }

  if (norm.includes('salida')) return { direccion: 'Salida', detalle: primera };
  if (norm.includes('ingreso')) return { direccion: 'Ingreso', detalle: primera };

  const detalle = obs.length > 120 ? `${obs.slice(0, 117)}…` : obs;
  return { direccion: null, detalle };
};

/** Columna Tipo en grilla. */
const getTipoEventoEnTabla = (evento) => {
  const tipoEvento = String(evento?.tipo_evento || '').toUpperCase();
  if (tipoEvento === 'ALFA_TANGO_2') {
    const { direccion } = parseAlfaTango2ResumenTabla(evento?.observacion);
    if (direccion === 'Ingreso') return 'Ingreso Alfa Tango 2';
    if (direccion === 'Salida') return 'Salida Alfa Tango 2';
    return 'Alfa Tango 2';
  }
  return evento?.tipo_evento || '—';
};

const resumenObservacionEnTabla = (evento) => {
  const tipoEvento = String(evento?.tipo_evento || '').toUpperCase();
  if (tipoEvento === 'ALFA_TANGO_2') {
    return parseAlfaTango2ResumenTabla(evento?.observacion).detalle;
  }
  const obs = String(evento?.observacion || '').trim();
  if (obs.length > 120) return `${obs.slice(0, 117)}…`;
  return obs || '—';
};

const eventoTieneEvidenciaFoto = (resolved) =>
  Boolean(resolved?.foto_url_resolved) ||
  (Array.isArray(resolved?.foto_urls_resolved) && resolved.foto_urls_resolved.length > 0) ||
  (Array.isArray(resolved?.foto_grupos_resolved) && resolved.foto_grupos_resolved.length > 0);

const getTipoEventoVisible = (evento) => {
  const tipoEvento = String(evento?.tipo_evento || '').toUpperCase();
  if (tipoEvento !== 'ALFA_TANGO_2') return evento?.tipo_evento || '—';

  const obs = String(evento?.observacion || '').toLowerCase();
  // Regla visual solicitada:
  // - Ingreso: Carga / Guía especial / Código Datafast
  // - Salida: Unidad / camión
  // Soporta tanto observaciones históricas como nuevas.
  if (obs.includes('ingreso - carga') || obs.includes('salida carga')) return 'Ingreso - Carga';
  if (obs.includes('ingreso - guía especial') || obs.includes('ingreso - guia especial') || obs.includes('guía especial') || obs.includes('guia especial')) return 'Ingreso - Guía especial';
  if (obs.includes('ingreso - código datafast') || obs.includes('ingreso - codigo datafast') || obs.includes('código datafast') || obs.includes('codigo datafast')) return 'Ingreso - Código Datafast';
  if (obs.includes('salida - unidad') || obs.includes('ingreso - unidad')) return 'Salida';
  if (obs.includes('alfa tango 2 - salida')) return 'Salida Alfa Tango 2';
  if (obs.includes('ingreso') && obs.includes('aire') && obs.includes('distrib') && !obs.includes('salida - z. distrib')) {
    return 'Ingreso - Aire a Distribución';
  }
  if (obs.includes('salida') && obs.includes('distrib') && /\bz\.\s*r\b/.test(obs) && !obs.includes('z. aire')) {
    return 'Salida - Distribución a R';
  }
  if (obs.includes('salida') && obs.includes('distrib') && obs.includes('transferencia')) {
    return 'Salida - Distribución a Transferencia';
  }
  if (obs.includes('salida') && obs.includes('distrib') && obs.includes('aire')) {
    return 'Salida - Distribución a Aire';
  }
  return 'Alfa Tango 2';
};

// Componente para detectar y mostrar imagen según orientación
const ImageWithOrientation = ({ src, alt }) => {
  const [orientation, setOrientation] = useState({ isVertical: false, isLoaded: false });
  const [imageError, setImageError] = useState(false);
  const imgRef = React.useRef(null);

  // Resetear estado cuando cambia el src
  useEffect(() => {
    setOrientation({ isVertical: false, isLoaded: false });
    setImageError(false);
    
    // Verificar si la imagen ya está cargada después de un pequeño delay
    const timer = setTimeout(() => {
      if (imgRef.current && imgRef.current.complete && imgRef.current.naturalHeight > 0) {
        const isVertical = imgRef.current.naturalHeight > imgRef.current.naturalWidth;
        setOrientation({ isVertical, isLoaded: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [src]);

  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalHeight === 0 || img.naturalWidth === 0) {
      // Imagen aún no cargada realmente
      return;
    }
    const isVertical = img.naturalHeight > img.naturalWidth;
    setOrientation({ isVertical, isLoaded: true });
    setImageError(false);
  };

  const handleImageError = () => {
    console.error('Error cargando imagen:', src);
    setImageError(true);
    setOrientation({ isVertical: false, isLoaded: true });
  };

  if (imageError) {
    return (
      <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
        <p className="text-sm text-gray-600">Error al cargar la imagen</p>
      </div>
    );
  }

  // Si es vertical, usar altura máxima más grande, si es horizontal, usar altura estándar
  const imageClass = orientation.isLoaded
    ? orientation.isVertical
      ? 'w-full max-h-64 object-contain rounded-lg'
      : 'w-full max-h-48 object-contain rounded-lg'
    : 'w-full object-contain rounded-lg opacity-0';

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center p-2 relative" style={{ minHeight: '200px' }}>
      {!orientation.isLoaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center space-y-2">
          <svg className="animate-spin h-6 w-6 text-gray-400" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-xs text-gray-500">Cargando...</p>
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={imageClass}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};

const ConsultasEventos = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [eventos, setEventos] = useState([]);
  const [filters, setFilters] = useState({
    fechaInicio: '',
    fechaFin: '',
    tipoEvento: '',
    observacion: ''
  });
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [tiposBusquedaPersonalizados, setTiposBusquedaPersonalizados] = useState([]);

  const apiOrigin = useMemo(() => {
    try {
      return new URL(getApiUrl('BITACORA', 'STATUS')).origin;
    } catch (error) {
      console.warn('No se pudo determinar origen de la API para consultas:', error);
      if (typeof window !== 'undefined') {
        return `${window.location.protocol}//${window.location.host}`;
      }
      return '';
    }
  }, []);

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  const opcionesTipoEvento = useMemo(() => {
    const extras = (tiposBusquedaPersonalizados || [])
      .filter((t) => typeof t === 'string' && t.trim())
      .map((value) => ({
        value: value.trim().toUpperCase(),
        label: TIPO_EVENTO_PERSONALIZADO_LABEL[value.trim().toUpperCase()] || value.trim().toUpperCase()
      }));
    extras.sort(
      (a, b) =>
        (ORDEN_TIPOS_PERSONALIZADOS.indexOf(a.value) === -1 ? 999 : ORDEN_TIPOS_PERSONALIZADOS.indexOf(a.value)) -
        (ORDEN_TIPOS_PERSONALIZADOS.indexOf(b.value) === -1 ? 999 : ORDEN_TIPOS_PERSONALIZADOS.indexOf(b.value))
    );
    return [...EVENT_TYPES_BASE, ...extras];
  }, [tiposBusquedaPersonalizados]);

  useEffect(() => {
    if (!user?.id_generado) {
      setTiposBusquedaPersonalizados([]);
      return undefined;
    }
    const url = new URL(getApiUrl('BITACORA', 'OPCIONES_PERSONALIZADAS'));
    url.searchParams.set('idGenerado', String(user.id_generado));
    let cancelled = false;
    fetch(url.toString(), { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success && data.data && Array.isArray(data.data.tiposBusquedaEventos)) {
          setTiposBusquedaPersonalizados(data.data.tiposBusquedaEventos);
        } else {
          setTiposBusquedaPersonalizados([]);
        }
      })
      .catch(() => {
        if (!cancelled) setTiposBusquedaPersonalizados([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_generado]);

  useEffect(() => {
    setFilters((prev) => {
      if (!prev.tipoEvento) return prev;
      if (!TIPOS_EVENTO_SOLO_CON_CATALOGO_PERSONALIZADO.has(prev.tipoEvento)) return prev;
      if (tiposBusquedaPersonalizados.includes(prev.tipoEvento)) return prev;
      return { ...prev, tipoEvento: '' };
    });
  }, [tiposBusquedaPersonalizados]);

  const fetchEventos = useCallback(
    async (showLoader = true) => {
      if (!user || !hasPuestoAsignado) return;

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');

      try {
        const url = new URL(getApiUrl('BITACORA', 'CONSULTAR_EVENTOS'));
        url.searchParams.set('idGenerado', user.id_generado);
        url.searchParams.set('username', user.username || '');
        url.searchParams.set('appid', user.appid || '');
        if (filters.fechaInicio) url.searchParams.set('fechaInicio', filters.fechaInicio);
        if (filters.fechaFin) url.searchParams.set('fechaFin', filters.fechaFin);
        if (filters.tipoEvento) url.searchParams.set('tipoEvento', filters.tipoEvento);
        if (filters.observacion) url.searchParams.set('observacion', filters.observacion);

        const response = await fetch(url.toString(), { credentials: 'include' });
        const data = await response.json();

        if (response.ok && data.success) {
          setEventos(Array.isArray(data.data) ? data.data : []);
          setStatus(data.meta || null);
        } else {
          setEventos([]);
          if (response.status === 403) {
            setError(data.error || 'No tiene permiso para filtrar por ese tipo de evento.');
          } else {
            setError(data.error || 'No fue posible consultar los eventos.');
          }
        }
      } catch (err) {
        console.error('Error consultando eventos:', err);
        setEventos([]);
        setError('Error de conexión al consultar los eventos.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters, hasPuestoAsignado, user]
  );

  useEffect(() => {
    if (!hasPuestoAsignado) {
      setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
      setLoading(false);
      return;
    }
    fetchEventos();
  }, [fetchEventos, hasPuestoAsignado]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await fetchEventos(false);
  };

  const handleResetFilters = () => {
    setFilters({
      fechaInicio: '',
      fechaFin: '',
      tipoEvento: '',
      observacion: ''
    });
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Consultar eventos</h1>
            <p className="text-gray-600">
              Filtra y consulta los eventos registrados en la bitácora de tu puesto.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchEventos(false)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
            disabled={loading || refreshing}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Consultando...</span>
              </span>
            ) : (
              'Actualizar resultados'
            )}
          </button>
        </div>

        {!hasPuestoAsignado ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            <p className="text-sm font-medium">
              Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
                  <input
                    type="date"
                    name="fechaInicio"
                    value={filters.fechaInicio}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
                  <input
                    type="date"
                    name="fechaFin"
                    value={filters.fechaFin}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de evento</label>
                  <select
                    name="tipoEvento"
                    value={filters.tipoEvento}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {opcionesTipoEvento.map((option) => (
                      <option key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
                  <input
                    type="text"
                    name="observacion"
                    value={filters.observacion}
                    onChange={handleInputChange}
                    placeholder="Buscar texto dentro de la observación"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Limpiar filtros
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Consultar
                </button>
              </div>
            </form>

            <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Resultados</h2>
                  <p className="text-sm text-gray-500">
                    {eventos.length > 0
                      ? `Se encontraron ${eventos.length} eventos que coinciden con los criterios.`
                      : 'No se encontraron eventos para los filtros seleccionados.'}
                  </p>
                </div>
                {status?.tablaBitacora && (
                  <span className="text-xs text-gray-500">
                    Tabla consultada: <strong>{status.tablaBitacora}</strong>
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Fecha y hora
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Observación
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {eventos.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                          No hay eventos para mostrar.
                        </td>
                      </tr>
                    ) : (
                      eventos.map((evento, index) => {
                        const hasDetails =
                          Boolean(String(evento.observacion || '').trim()) ||
                          Boolean(String(evento.p_nombre || '').trim()) ||
                          Boolean(String(evento.v_placa || '').trim()) ||
                          tieneFotoUrlUtil(evento.foto_url) ||
                          Boolean(String(evento.foto_firma_url || '').trim());

                        return (
                          <tr key={`${evento.id || evento.tipo_evento}-${evento.hora_registro_servidor}-${index}`}>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatFechaHoraConsulta(evento.hora_registro_servidor) || '—'}
                              {formatFechaHoraConsulta(evento.hora_reporte) ? (
                                <div className="text-xs text-gray-500 mt-1">
                                  Hora real: {formatFechaHoraConsulta(evento.hora_reporte)}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-indigo-600">
                              <span className="inline-flex items-center gap-2">
                                {getTipoEventoEnTabla(evento)}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!hasDetails) return;
                                  const resolved = getResolvedFotosForEvento(evento, apiOrigin);
                                  setSelectedEvento({
                                    ...evento,
                                    ...resolved
                                  });
                                  }}
                                  className={`p-1.5 rounded-full border border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    hasDetails
                                      ? 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-500'
                                      : 'text-gray-300 cursor-not-allowed focus:ring-transparent'
                                  }`}
                                  title={
                                    hasDetails ? 'Ver detalles del evento' : 'No hay detalles adicionales para este evento'
                                  }
                                  disabled={!hasDetails}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                                    />
                                  </svg>
                                </button>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {resumenObservacionEnTabla(evento)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      {selectedEvento && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-60 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden my-4 sm:my-0">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Detalles del evento</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {getTipoEventoVisible(selectedEvento) || 'Evento'} ·{' '}
                  {selectedEvento.hora_registro_servidor
                    ? new Date(selectedEvento.hora_registro_servidor).toLocaleString('es-EC', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEvento(null)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1.5 sm:p-1 flex-shrink-0"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-4 sm:px-5 py-4 space-y-5 overflow-y-auto flex-1">
              {(() => {
                const detalleAt2 =
                  parseObservacionSalidaDistR(selectedEvento.observacion) ||
                  parseObservacionSalidaDistAlfa(selectedEvento.observacion) ||
                  parseObservacionSalidaDistTransferencia(selectedEvento.observacion) ||
                  parseObservacionSalidaDistAire(selectedEvento.observacion) ||
                  parseObservacionIngresoAlfaDistrib(selectedEvento.observacion) ||
                  parseObservacionIngresoAireDistrib(selectedEvento.observacion);
                const tieneFotos = eventoTieneEvidenciaFoto(selectedEvento);
                const tieneFirma = Boolean(selectedEvento.foto_firma_url_resolved);
                const mostrarNombrePlaca =
                  Boolean(String(selectedEvento.p_nombre || '').trim()) ||
                  Boolean(String(selectedEvento.v_placa || '').trim());

                if (detalleAt2) {
                  return (
                    <>
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
                        <p className="text-sm font-semibold text-indigo-900">{detalleAt2.titulo}</p>
                      </div>

                      <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                        {detalleAt2.campos.map((campo) => (
                          <div
                            key={campo.label}
                            className="grid grid-cols-1 sm:grid-cols-[minmax(9rem,34%)_1fr] gap-1 sm:gap-4 px-4 py-3.5"
                          >
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              {campo.label}
                            </p>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{campo.value}</p>
                          </div>
                        ))}
                      </div>

                      {detalleAt2.observacionesLibre ? (
                        <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
                            Observaciones
                          </p>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                            {detalleAt2.observacionesLibre}
                          </p>
                        </div>
                      ) : null}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Hora ingreso</p>
                          <p className="mt-1 text-sm font-medium text-gray-900">
                            {selectedEvento.hora_ingreso || '—'}
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Hora salida</p>
                          <p className="mt-1 text-sm font-medium text-gray-900">
                            {selectedEvento.hora_salida || '—'}
                          </p>
                        </div>
                      </div>

                      {tieneFotos ? (
                        <div className="space-y-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Fotos / evidencias</p>
                          {Array.isArray(selectedEvento.foto_grupos_resolved) &&
                          selectedEvento.foto_grupos_resolved.length > 0 ? (
                            <div className="space-y-4">
                              {selectedEvento.foto_grupos_resolved.map((grupo) => (
                                <div key={grupo.slot} className="space-y-2">
                                  <p className="text-xs font-semibold text-gray-700">{grupo.label}</p>
                                  <div className="grid grid-cols-1 gap-3">
                                    {grupo.urls.map((src, idx) => (
                                      <ImageWithOrientation
                                        key={`${grupo.slot}-${src}-${idx}`}
                                        src={src}
                                        alt={`${grupo.label} ${idx + 1}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : Array.isArray(selectedEvento.foto_urls_resolved) &&
                            selectedEvento.foto_urls_resolved.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3">
                              {selectedEvento.foto_urls_resolved.map((src, idx) => (
                                <ImageWithOrientation
                                  key={`${src}-${idx}`}
                                  src={src}
                                  alt={`Evidencia ${idx + 1}`}
                                />
                              ))}
                            </div>
                          ) : selectedEvento.foto_url_resolved ? (
                            <ImageWithOrientation
                              src={selectedEvento.foto_url_resolved}
                              alt="Evidencia asociada"
                            />
                          ) : null}
                        </div>
                      ) : null}

                      {tieneFirma ? (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Firma registrada</p>
                          <img
                            src={selectedEvento.foto_firma_url_resolved}
                            alt="Firma asociada"
                            className="w-full max-h-48 object-contain rounded-lg border border-gray-200 bg-white"
                          />
                        </div>
                      ) : null}
                    </>
                  );
                }

                return (
                  <>
                    {formatFechaHoraConsulta(selectedEvento.hora_reporte) ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Hora real del reporte</p>
                          <p className="mt-1 text-sm font-medium text-gray-900">
                            {formatFechaHoraConsulta(selectedEvento.hora_reporte)}
                          </p>
                        </div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Registrado en servidor</p>
                          <p className="mt-1 text-sm font-medium text-gray-900">
                            {formatFechaHoraConsulta(selectedEvento.hora_registro_servidor) || '—'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Observación</p>
                      <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words">
                        {selectedEvento.observacion || '—'}
                      </p>
                    </div>

                    {mostrarNombrePlaca ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Nombre asociado</p>
                          <p className="mt-1 text-sm text-gray-800">{selectedEvento.p_nombre || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Placa vehículo</p>
                          <p className="mt-1 text-sm text-gray-800">{selectedEvento.v_placa || '—'}</p>
                        </div>
                      </div>
                    ) : null}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Hora ingreso</p>
                        <p className="mt-1 text-sm text-gray-800">{selectedEvento.hora_ingreso || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Hora salida</p>
                        <p className="mt-1 text-sm text-gray-800">{selectedEvento.hora_salida || '—'}</p>
                      </div>
                    </div>

                    {(tieneFotos || tieneFirma) && (
                      <div
                        className={`grid grid-cols-1 gap-4 ${
                          tieneFotos && tieneFirma ? 'sm:grid-cols-2' : ''
                        }`}
                      >
                        {tieneFotos ? (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                              Identificación / Foto
                            </p>
                            {Array.isArray(selectedEvento.foto_grupos_resolved) &&
                            selectedEvento.foto_grupos_resolved.length > 0 ? (
                              <div className="space-y-4">
                                {selectedEvento.foto_grupos_resolved.map((grupo) => (
                                  <div key={grupo.slot} className="space-y-2">
                                    <p className="text-xs font-semibold text-gray-700">{grupo.label}</p>
                                    <div className="grid grid-cols-1 gap-3">
                                      {grupo.urls.map((src, idx) => (
                                        <ImageWithOrientation
                                          key={`${grupo.slot}-${src}-${idx}`}
                                          src={src}
                                          alt={`${grupo.label} ${idx + 1}`}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : Array.isArray(selectedEvento.foto_urls_resolved) &&
                              selectedEvento.foto_urls_resolved.length > 0 ? (
                              <div className="grid grid-cols-1 gap-3">
                                {selectedEvento.foto_urls_resolved.map((src, idx) => (
                                  <ImageWithOrientation
                                    key={`${src}-${idx}`}
                                    src={src}
                                    alt={`Evidencia ${idx + 1}`}
                                  />
                                ))}
                              </div>
                            ) : selectedEvento.foto_url_resolved ? (
                              <ImageWithOrientation
                                src={selectedEvento.foto_url_resolved}
                                alt="Identificación asociada"
                              />
                            ) : null}
                          </div>
                        ) : null}
                        {tieneFirma ? (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Firma registrada</p>
                            <img
                              src={selectedEvento.foto_firma_url_resolved}
                              alt="Firma asociada"
                              className="w-full h-48 object-contain rounded-lg border border-gray-200 bg-white"
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="px-4 sm:px-5 py-3 bg-gray-50 border-t border-gray-200 flex-shrink-0 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSelectedEvento(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 w-full sm:w-auto justify-center"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ConsultasEventos;

